import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import {
  Box, Paper, Typography, Button, CircularProgress, Chip, Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const API   = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/liberty-medical-invoice-confirm`;
const VIEWS = { LOADING: "loading", TRACKS: "tracks", ALL_DONE: "all_done", ERROR: "error", IDLE: "idle" };

export default function LibertyMedicalConfirmPage() {
  const { instance, accounts } = useMsal();
  const [view, setView]         = useState(VIEWS.LOADING);
  const [tracks, setTracks]     = useState([]);
  const [confirming, setConfirming] = useState(null); // docno currently being confirmed
  const [errorMsg, setErrorMsg] = useState("");

  const getAuthHeader = useCallback(async () => {
    const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return { Authorization: `Bearer ${result.accessToken}` };
  }, [instance, accounts]);

  const loadTracks = useCallback(async () => {
    setView(VIEWS.LOADING);
    try {
      const res = await fetch(API, { headers: await getAuthHeader() });
      if (res.status === 404) { setView(VIEWS.IDLE); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTracks(data.tracks || []);
      setView(data.tracks?.length ? VIEWS.TRACKS : VIEWS.IDLE);
    } catch {
      setView(VIEWS.ERROR);
      setErrorMsg("שגיאה בטעינת התור.");
    }
  }, [getAuthHeader]);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  const handleConfirm = async (docno) => {
    setConfirming(docno);
    setErrorMsg("");
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { ...(await getAuthHeader()), "Content-Type": "application/json" },
        body: JSON.stringify({ docno }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(
          data.invoice
            ? `חשבונית ${data.invoice} לא נוצרה — ייתכן שתעודה ${docno} עדיין פתוחה בפריוריטי.`
            : "שגיאה ביצירת החשבונית הבאה."
        );
        return;
      }
      if (data.status === "all_done") {
        setView(VIEWS.ALL_DONE);
        return;
      }
      // Remove confirmed track; update waiting_count if next was created
      setTracks(prev => prev
        .map(t => {
          if (t.docno !== docno) return t;
          if (!data.next_invoice) return null; // track exhausted
          return { ...t, invoice_number: data.next_invoice, waiting_count: t.waiting_count - 1 };
        })
        .filter(Boolean)
      );
    } catch {
      setErrorMsg("שגיאת רשת. נסה שוב.");
    } finally {
      setConfirming(null);
    }
  };

  return (
    <Box sx={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Paper elevation={3} sx={{ width: "100%", maxWidth: 600, borderRadius: 3, p: { xs: 3, sm: "36px 40px" } }}>
        {view === VIEWS.LOADING  && <LoadingView />}
        {view === VIEWS.IDLE     && <IdleView />}
        {view === VIEWS.ALL_DONE && <AllDoneView />}
        {view === VIEWS.ERROR    && <ErrorView message={errorMsg || "שגיאה בטעינת התור."} onRetry={loadTracks} />}
        {view === VIEWS.TRACKS   && (
          <TracksView
            tracks={tracks}
            confirming={confirming}
            errorMsg={errorMsg}
            onConfirm={handleConfirm}
          />
        )}
      </Paper>
    </Box>
  );
}

function LoadingView() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
      <CircularProgress sx={{ color: "#1a3a5c" }} />
    </Box>
  );
}

function IdleView() {
  return (
    <Box sx={{ textAlign: "center", py: 2 }}>
      <InfoOutlinedIcon sx={{ fontSize: 56, color: "#1a3a5c", mb: 1 }} />
      <Typography fontWeight={700} fontSize={18} color="#1a3a5c" gutterBottom>
        אין תהליך פעיל כרגע
      </Typography>
      <Typography color="text.secondary" fontSize={14}>
        הדף מופעל כאשר פיפליין Liberty Medical שולח חשבוניות לאישור.
      </Typography>
    </Box>
  );
}

function AllDoneView() {
  return (
    <Box sx={{ textAlign: "center", py: 2 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#2e7d32", mb: 1 }} />
      <Typography fontWeight={700} fontSize={18} color="#2e7d32" gutterBottom>כל החשבוניות עובדו בהצלחה</Typography>
      <Typography color="text.secondary" fontSize={14}>אין עוד חשבוניות בתור.</Typography>
    </Box>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <Box sx={{ textAlign: "center", py: 2 }}>
      <ErrorOutlineIcon sx={{ fontSize: 56, color: "#c62828", mb: 1 }} />
      <Typography fontWeight={700} fontSize={18} color="#c62828" gutterBottom>שגיאה</Typography>
      <Typography color="text.secondary" fontSize={14} mb={3}>{message}</Typography>
      {onRetry && <Button variant="outlined" color="error" onClick={onRetry}>נסה שוב</Button>}
    </Box>
  );
}

function TracksView({ tracks, confirming, errorMsg, onConfirm }) {
  return (
    <>
      <Typography fontSize={13} color="text.secondary" mb={1}>
        Liberty Medical · קליטת חשבוניות ספק
      </Typography>
      <Typography variant="h6" fontWeight={700} color="#1a3a5c" mb={3}>
        חשבוניות הממתינות לאישור
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {tracks.map((track, i) => (
          <TrackCard
            key={track.docno}
            track={track}
            index={i + 1}
            total={tracks.length}
            isConfirming={confirming === track.docno}
            disabled={!!confirming}
            onConfirm={() => onConfirm(track.docno)}
          />
        ))}
      </Box>

      {errorMsg && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
          <Typography fontSize={13} color="#c62828">{errorMsg}</Typography>
        </Box>
      )}
    </>
  );
}

function TrackCard({ track, index, total, isConfirming, disabled, onConfirm }) {
  return (
    <Box sx={{ border: "1px solid #e3eaf5", borderRadius: 2, p: 2.5, bgcolor: "#fafcff" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Chip
          label={`${index} / ${total}`}
          size="small"
          sx={{ bgcolor: "#e3eaf5", color: "#1a3a5c", fontWeight: 600 }}
        />
        {track.waiting_count > 0 && (
          <Typography fontSize={12} color="text.secondary">
            + עוד {track.waiting_count} בהמתנה באותה תעודה
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
        <Field
          label={track.docnos?.length > 1 ? "תעודות" : "תעודה"}
          value={track.docnos?.length ? track.docnos.join(", ") : track.docno}
        />
        <Field label="חשבונית" value={track.invoice_number} />
        <Field label="תיק יבוא" value={track.import_file} />
        <Field label="שורות קבלת סחורה" value={track.gr_count} />
      </Box>

      <Divider sx={{ mb: 2 }} />
      <Box sx={{ bgcolor: "#fff8e1", borderRight: "4px solid #f59f00", borderRadius: 1, px: 2, py: 1.5, mb: 2 }}>
        <Typography fontSize={13}>
          סגור את החשבונית <strong>ידנית בפריוריטי</strong>
          {track.docnos?.length > 1 ? " (ודא שכל התעודות המפורטות למעלה מקושרות)" : ""} ולאחר מכן לחץ אישור.
        </Typography>
      </Box>

      <Button
        variant="contained" fullWidth disabled={disabled} onClick={onConfirm}
        sx={{
          bgcolor: "#2e7d32", fontWeight: 700, py: 1.2, borderRadius: 2,
          "&:hover": { bgcolor: "#1b5e20" },
          "&:disabled": { bgcolor: "#a5d6a7", color: "#fff" },
        }}
      >
        {isConfirming
          ? <CircularProgress size={20} sx={{ color: "#fff" }} />
          : `✔ אישור — סגרתי חשבונית ${track.invoice_number}`}
      </Button>
    </Box>
  );
}

function Field({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <Typography fontSize={12} color="text.secondary" fontWeight={600}>{label}</Typography>
      <Typography fontSize={15} fontWeight={700} color="#1a3a5c">{value ?? "—"}</Typography>
    </Box>
  );
}
